/**
 * Thin fetch wrapper that handles auth automatically.
 *
 * - Reads access/refresh tokens from localStorage on each call
 * - Attaches Authorization: Bearer <access> if present
 * - On 401 with an expired access token, tries once to refresh and retry
 * - Throws ApiError on non-2xx so callers can await/try-catch
 */

const ACCESS_KEY = 'nh_access';
const REFRESH_KEY = 'nh_refresh';

// In dev, Vite proxies /api to the backend. In prod, Apache does the same.
// So we just always use relative URLs. If you ever need to override, set
// VITE_API_BASE in the environment.
const API_BASE = import.meta.env.VITE_API_BASE || '';


export class ApiError extends Error {
  constructor(status, detail, payload) {
    super(detail || `HTTP ${status}`);
    this.status = status;
    this.detail = detail;
    this.payload = payload;
  }
}


export function getAccessToken() {
  return localStorage.getItem(ACCESS_KEY);
}

export function getRefreshToken() {
  return localStorage.getItem(REFRESH_KEY);
}

export function setTokens({ access_token, refresh_token }) {
  if (access_token) localStorage.setItem(ACCESS_KEY, access_token);
  if (refresh_token) localStorage.setItem(REFRESH_KEY, refresh_token);
}

export function clearTokens() {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
}


async function tryRefresh() {
  const refresh_token = getRefreshToken();
  if (!refresh_token) return false;

  const res = await fetch(`${API_BASE}/api/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token }),
  });

  if (!res.ok) return false;

  const data = await res.json();
  if (!data.access_token) return false;

  setTokens({ access_token: data.access_token });
  return true;
}


/**
 * Core request function. Call the helper methods below instead.
 */
async function request(method, path, body, { skipAuth = false, retryOn401 = true } = {}) {
  const isFormData = body instanceof FormData;
  const headers = {};
  if (!isFormData) headers['Content-Type'] = 'application/json';
  if (!skipAuth) {
    const token = getAccessToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : isFormData ? body : JSON.stringify(body),
  });

  // Access token expired — try one refresh + retry.
  if (res.status === 401 && retryOn401 && !skipAuth) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      return request(method, path, body, { skipAuth, retryOn401: false });
    }
    clearTokens();
  }

  // Some endpoints (e.g. logout) return 204 with no body.
  if (res.status === 204) return null;

  let payload = null;
  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    try { payload = await res.json(); } catch { payload = null; }
  }

  if (!res.ok) {
    const detail = payload?.detail ?? `HTTP ${res.status}`;
    throw new ApiError(res.status, detail, payload);
  }

  return payload;
}


export const api = {
  get:  (path, opts)       => request('GET',    path, undefined, opts),
  post: (path, body, opts) => request('POST',   path, body,       opts),
  put:  (path, body, opts) => request('PUT',    path, body,       opts),
  patch:(path, body, opts) => request('PATCH',  path, body,       opts),
  del:  (path, opts)       => request('DELETE', path, undefined, opts),
};
